import { useState, useEffect, useRef } from 'react';
import { TelemedicineParticipant } from '@/api/telemedicine-api';
import { Camera, CameraOff, MicOff, Monitor, Shield, Users } from 'lucide-react';

interface VideoStageProps {
  participants: TelemedicineParticipant[];
  localUserId?: string;
  cameraOn: boolean;
  microphoneOn?: boolean;
  screenShareOn: boolean;
  connectionState: 'CONNECTING' | 'CONNECTED' | 'RECONNECTING' | 'DISCONNECTED';
  roomUrl?: string;
  isRecording?: boolean;
}

export function VideoStage({
  participants,
  localUserId,
  cameraOn,
  microphoneOn = true,
  screenShareOn,
  connectionState,
  roomUrl,
  isRecording = false,
}: VideoStageProps) {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const screenVideoRef = useRef<HTMLVideoElement>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);

  const [streamReady, setStreamReady] = useState(false);
  const [deviceError, setDeviceError] = useState<string | null>(null);

  const remoteParticipants = participants.filter(p => p.userId !== localUserId);
  const hasRemote = remoteParticipants.length > 0;
  const isDailyUrl = Boolean(roomUrl && (roomUrl.startsWith('http://') || roomUrl.startsWith('https://')));

  // Inicializa o fluxo de câmera e microfone real no navegador quando não for sala Daily.co
  useEffect(() => {
    // Se a sala for Daily.co, o próprio iframe do Daily gerencia a câmera e o microfone.
    // Chamar getUserMedia aqui causaria erro NotReadableError (dispositivo ocupado) no Windows/Android.
    if (isDailyUrl) return;

    let isMounted = true;

    async function initMedia() {
      if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;

      try {
        console.info('[VIDEO-STAGE] Solicitando permissões de câmera e microfone...');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: true,
        });

        if (!isMounted) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        console.info('[VIDEO-STAGE] ✓ Câmera e microfone concedidos com sucesso. Tracks:', stream.getTracks().map(t => `${t.kind}:${t.label}`));
        localStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        setStreamReady(true);
        setDeviceError(null);
      } catch (err: any) {
        console.warn('[VIDEO-STAGE] Não foi possível acessar câmera/microfone:', err?.message || err);
        if (isMounted) {
          setDeviceError('Câmera ou microfone ocupado/bloqueado');
        }
      }
    }

    initMedia();

    return () => {
      isMounted = false;
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(t => t.stop());
        localStreamRef.current = null;
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => t.stop());
        screenStreamRef.current = null;
      }
    };
  }, [isDailyUrl]);

  // Controla o estado de ligar/desligar a câmera local
  useEffect(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(track => {
        track.enabled = cameraOn;
      });
    }
  }, [cameraOn]);

  // Controla o estado de mutar/desmutar o microfone local
  useEffect(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = microphoneOn;
      });
    }
  }, [microphoneOn]);

  // Controla o compartilhamento de tela real
  useEffect(() => {
    if (screenShareOn) {
      if (navigator.mediaDevices?.getDisplayMedia) {
        navigator.mediaDevices.getDisplayMedia({ video: true })
          .then(stream => {
            screenStreamRef.current = stream;
            if (screenVideoRef.current) {
              screenVideoRef.current.srcObject = stream;
            }
            stream.getVideoTracks()[0]!.onended = () => {
              if (screenStreamRef.current) {
                screenStreamRef.current.getTracks().forEach(t => t.stop());
                screenStreamRef.current = null;
              }
            };
          })
          .catch(() => {});
      }
    } else {
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => t.stop());
        screenStreamRef.current = null;
      }
    }
  }, [screenShareOn]);

  const stateColor = {
    CONNECTING: 'bg-yellow-500',
    CONNECTED: 'bg-green-500',
    RECONNECTING: 'bg-orange-500',
    DISCONNECTED: 'bg-red-500',
  }[connectionState];

  const stateLabel = {
    CONNECTING: 'Conectando...',
    CONNECTED: 'Conectado',
    RECONNECTING: 'Reconectando...',
    DISCONNECTED: 'Desconectado',
  }[connectionState];

  // Se houver Daily.co configurado e for URL externa ativa
  if (isDailyUrl && roomUrl) {
    // Garante que o idioma seja Português e que o chat nativo redundante do Daily seja ocultado
    let cleanRoomUrl = roomUrl;
    if (!cleanRoomUrl.includes('chat=')) {
      cleanRoomUrl += `${cleanRoomUrl.includes('?') ? '&' : '?'}chat=0`;
    }
    if (!cleanRoomUrl.includes('lang=')) {
      cleanRoomUrl += `${cleanRoomUrl.includes('?') ? '&' : '?'}lang=pt`;
    }

    return (
      <div className="relative bg-gray-950 rounded-none sm:rounded-2xl overflow-hidden w-full h-full shadow-lg border border-neutral-800">
        <iframe
          src={cleanRoomUrl}
          allow="camera; microphone; fullscreen; display-capture; autoplay"
          className="w-full h-full border-0"
          title="Daily.co Video Call Room"
        />
      </div>
    );
  }

  return (
    <div className="relative bg-neutral-950 rounded-none sm:rounded-2xl overflow-hidden w-full h-full flex items-center justify-center shadow-lg border border-neutral-800">
      {/* Compartilhamento de tela em foco se ativo */}
      {screenShareOn && (
        <div className="absolute inset-0 z-10 bg-black flex items-center justify-center">
          <video
            ref={screenVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-contain"
          />
          <div className="absolute top-4 left-4 px-3 py-1 bg-blue-600/90 text-white rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-md">
            <Monitor className="w-3.5 h-3.5" />
            <span>Sua tela compartilhada</span>
          </div>
        </div>
      )}

      {/* Palco Principal: Participante Remoto ou Aguardo */}
      {hasRemote ? (
        <div className="w-full h-full flex flex-col items-center justify-center text-white p-6 bg-gradient-to-b from-neutral-900 to-neutral-950">
          <div className="text-center space-y-3">
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-gradient-to-tr from-blue-600 via-indigo-600 to-purple-600 mx-auto flex items-center justify-center text-3xl sm:text-4xl font-extrabold shadow-2xl ring-4 ring-white/10 animate-pulse">
              {remoteParticipants[0]!.role === 'DOCTOR' ? 'Dr.' : 'Pac.'}
            </div>
            <div>
              <p className="text-lg font-bold text-white tracking-wide">
                {remoteParticipants[0]!.role === 'DOCTOR' ? 'Médico Conectado' : 'Paciente Conectado'}
              </p>
              <p className="text-xs text-neutral-400 mt-0.5">
                Canal criptografado de áudio e vídeo ativo
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-white text-center p-6 space-y-3">
          <div className="w-16 h-16 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center mx-auto text-primary">
            <Users className="w-8 h-8 animate-pulse text-blue-400" />
          </div>
          <div>
            <p className="text-base sm:text-lg font-semibold">Aguardando outro participante entrar...</p>
            <p className="text-xs text-neutral-400 mt-1">
              A chamada iniciará automaticamente assim que a outra parte se conectar.
            </p>
          </div>
        </div>
      )}

      {/* Câmera Local (Picture-in-Picture no canto inferior direito) */}
      <div className="absolute bottom-20 right-3 sm:bottom-4 sm:right-4 w-28 h-20 sm:w-44 sm:h-32 bg-black/90 rounded-xl overflow-hidden border-2 border-white/20 shadow-2xl z-20 group">
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover transition-opacity duration-200 ${
            cameraOn && streamReady ? 'opacity-100 scale-x-[-1]' : 'opacity-0'
          }`}
        />

        {(!cameraOn || !streamReady) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-neutral-400 bg-neutral-900/95 p-2 text-center">
            <CameraOff className="w-5 h-5 mb-1 text-neutral-500" />
            <span className="text-[11px] font-medium">
              {deviceError ? 'Dispositivo ocupado' : 'Câmera desativada'}
            </span>
          </div>
        )}

        <div className="absolute bottom-1.5 left-2 right-2 flex items-center justify-between text-[10px] text-white/90 bg-black/60 px-2 py-0.5 rounded backdrop-blur-xs">
          <span className="font-semibold truncate">Você</span>
          <div className="flex items-center gap-1">
            {!microphoneOn && <MicOff className="w-3 h-3 text-red-400" />}
            {cameraOn ? <Camera className="w-3 h-3 text-emerald-400" /> : <CameraOff className="w-3 h-3 text-neutral-400" />}
          </div>
        </div>
      </div>

      {/* Status da Conexão e Criptografia (Canto Superior Esquerdo) */}
      <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1 bg-black/60 backdrop-blur-md rounded-full text-white text-xs border border-white/10 shadow-sm z-20">
        <span className={`w-2 h-2 rounded-full ${stateColor}`} />
        <span className="font-medium">{stateLabel}</span>
        {isRecording && (
          <div className="flex items-center gap-1 ml-1 pl-2 border-l border-white/20">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-ping" />
            <span className="text-red-400 font-semibold text-[11px]">REC</span>
          </div>
        )}
      </div>

      {/* Selo de Sala Criptografada (Canto Superior Direito) */}
      <div className="absolute top-3 right-3 px-3 py-1 bg-black/60 backdrop-blur-md rounded-full text-white text-xs border border-white/10 shadow-sm flex items-center gap-1.5 z-20">
        <Shield className="w-3.5 h-3.5 text-emerald-400" />
        <span className="text-[11px] font-medium">Criptografia AES-256</span>
      </div>
    </div>
  );
}
